const FILE_EXTENSIONS = [
  "docx", "dotx", "doc", "dot", "xlsx", "xlsm", "xlam", "xltx", "xls", "xlt",
  "pptx", "pptm", "ppsx", "potx", "ppt", "pps", "pot", "pdf", "txt", "rtf",
  "csv", "tsv", "odt", "ods", "odp", "msg", "eml", "json", "xml", "zip",
  "rar", "7z", "html", "htm", "log", "jpg", "jpeg", "png", "tif", "tiff",
];

const URL_OR_EMAIL = /^(?:https?:\/\/|www\.|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|(?:[\w-]+\.)+[A-Za-z]{2,}(?:[/?#].*)?$)/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SEPARATOR = /(_+|-+|,+|[()]|(?<!\d):(?!\d)|(?<!\d)\.|\.(?!\d))/g;

function splitWord(value, extensions) {
  if (!value || /^<<<[^>]+>>>$/.test(value)) return value ? [value] : [];

  const extension = extensions
    .map((item) => item.replace(/^\./, ""))
    .join("|");
  const extensionAtEnd = new RegExp(`^(.+)(\\.(${extension}))$`, "i").exec(value);
  if (extensionAtEnd) {
    return [...splitWord(extensionAtEnd[1], extensions), extensionAtEnd[2]];
  }
  if (UUID.test(value)) return [value];
  if (URL_OR_EMAIL.test(value)) return [value];

  return value.split(SEPARATOR).filter(Boolean);
}

export function splitStringSegment(value, extensions = FILE_EXTENSIONS) {
  if (!value) return [];
  const chunks = String(value).split(/(\s+)/).filter(Boolean);
  const result = [];
  for (const chunk of chunks) {
    if (/^\s+$/.test(chunk) && result.length) result[result.length - 1] += chunk;
    else result.push(...splitWord(chunk, extensions));
  }
  return result;
}

export function splitString(value, extensions = FILE_EXTENSIONS) {
  if (!value) return [];
  return String(value)
    .split("<<<space>>>")
    .flatMap((segment) => splitStringSegment(segment, extensions));
}

export function getSpaceBoundaryIndices(value, extensions = FILE_EXTENSIONS) {
  if (!value) return new Set();
  const boundaries = new Set();
  let runningIndex = 0;
  const segments = String(value).split("<<<space>>>");
  segments.forEach((segment, index) => {
    runningIndex += splitStringSegment(segment, extensions).length;
    if (index < segments.length - 1) boundaries.add(runningIndex - 1);
  });
  return boundaries;
}

// Maps each original token to the index of its anonymized token. A placeholder
// can cover several original words ("Sadaf Hussain" -> "<<<NAME>>>"), so a plain
// position match would shift every following word. Returns null if the two
// texts do not fit together, callers then fall back to the position.
export function alignAnonymizedIndices(originalTokens, anonymizedTokens, isPlaceholder) {
  const memo = new Map();
  function align(originalIndex, anonymizedIndex) {
    const key = `${originalIndex}:${anonymizedIndex}`;
    if (memo.has(key)) return memo.get(key);
    let result = null;
    if (anonymizedIndex === anonymizedTokens.length) {
      result = originalIndex === originalTokens.length ? [] : null;
    } else if (isPlaceholder(anonymizedTokens[anonymizedIndex])) {
      const remainingTokens = anonymizedTokens.length - anonymizedIndex - 1;
      for (let end = originalIndex + 1; end <= originalTokens.length - remainingTokens; end += 1) {
        const tail = align(end, anonymizedIndex + 1);
        if (tail) {
          result = [...Array(end - originalIndex).fill(anonymizedIndex), ...tail];
          break;
        }
      }
    } else if (
      originalIndex < originalTokens.length
      && originalTokens[originalIndex].trim() === anonymizedTokens[anonymizedIndex].trim()
    ) {
      const tail = align(originalIndex + 1, anonymizedIndex + 1);
      if (tail) result = [anonymizedIndex, ...tail];
    }
    memo.set(key, result);
    return result;
  }
  return align(0, 0);
}
